import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * Loop3 Tag — compact mono-typeset label used for status badges, metadata
 * chips, and stateful eyebrows. NOT a user-interactive element (use Button
 * for that).
 *
 * Variants:
 *   default  — neutral thin-border tag
 *   pulse    — violet with a pulsing dot (use for coming-soon / in-progress)
 *   complete — amber (reserved for completion/success states)
 *   muted    — low-contrast, no dot
 */
const tagVariants = cva(
  'inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] whitespace-nowrap',
  {
    variants: {
      variant: {
        default:
          'text-foreground/70 border border-foreground/15 rounded-full px-2 py-0.5 text-[0.6rem]',
        pulse: 'text-[var(--l3-violet)] text-[0.6rem]',
        complete: 'text-[var(--accent)] text-[0.6rem]',
        muted: 'text-foreground/50 text-[0.6rem]',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface TagProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof tagVariants> {
  /** Render a small colored dot before the label. Auto-pulses when variant="pulse". */
  dot?: boolean;
}

export function Tag({
  className,
  variant = 'default',
  dot,
  children,
  ...props
}: TagProps) {
  const showDot = dot ?? variant === 'pulse';
  const dotColor =
    variant === 'pulse'
      ? 'bg-[var(--l3-violet)]'
      : variant === 'complete'
        ? 'bg-[var(--accent)]'
        : 'bg-foreground/40';

  return (
    <span
      data-slot="tag"
      className={cn(tagVariants({ variant }), className)}
      {...props}
    >
      {showDot && (
        <span
          aria-hidden
          className={cn(
            'size-1 rounded-full',
            dotColor,
            variant === 'pulse' && 'animate-pulse',
          )}
        />
      )}
      {children}
    </span>
  );
}

export { tagVariants };
